/**
 * On-device Kokoro TTS. Runs entirely in the phone's browser via
 * transformers.js + onnxruntime-web.
 *
 * Requires WebGPU for usable speed:
 *   - WebGPU (iOS 26+, modern Chrome): ~2-5s per sentence on iPhone 15 A16
 *   - WASM fallback: ~60s per sentence - we refuse to run it
 *
 * Model weights come from HF CDN (static files, no telemetry). After first
 * download the browser caches them - works fully offline on subsequent plays.
 */

// NOTE: @huggingface/transformers is imported DYNAMICALLY inside loadOnDeviceKokoro
// to avoid eagerly loading onnxruntime-web at module evaluation time.
// onnxruntime-web crashes on iOS Safari if SharedArrayBuffer is unavailable.

export type OnDeviceStatus = 'unknown' | 'webgpu' | 'wasm' | 'unavailable';

let ttsPromise: Promise<any> | null = null;
let detectedStatus: OnDeviceStatus = 'unknown';

export async function detectOnDeviceCapability(): Promise<OnDeviceStatus> {
  if (detectedStatus !== 'unknown') return detectedStatus;
  if (typeof navigator === 'undefined' || !(navigator as any).gpu) {
    detectedStatus = 'wasm';
    return detectedStatus;
  }
  try {
    const adapter = await (navigator as any).gpu.requestAdapter();
    if (adapter) {
      detectedStatus = 'webgpu';
      return detectedStatus;
    }
  } catch {}
  detectedStatus = 'wasm';
  return detectedStatus;
}

/** Full diagnostic report for visible debugging. */
export async function getDiagnostics(): Promise<Record<string, any>> {
  const nav: any = navigator;
  const info: Record<string, any> = {
    userAgent: (nav.userAgent || '').slice(0, 100),
    isStandalonePWA: (nav.standalone === true) || window.matchMedia('(display-mode: standalone)').matches,
    hasNavigatorGpu: Boolean(nav.gpu),
    hasSharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    isCrossOriginIsolated: typeof (window as any).crossOriginIsolated === 'boolean' ? (window as any).crossOriginIsolated : 'unknown',
    hasAudioContext: Boolean((window as any).AudioContext || (window as any).webkitAudioContext),
  };
  if (nav.gpu) {
    try {
      const adapter = await nav.gpu.requestAdapter();
      info.adapter = adapter ? 'got adapter' : 'requestAdapter returned null';
      if (adapter) {
        try {
          const a: any = adapter;
          info.adapterVendor = a.info?.vendor || 'unknown';
          info.adapterArch = a.info?.architecture || 'unknown';
        } catch {}
      }
    } catch (e: any) {
      info.adapterError = String(e?.message || e);
    }
  }
  return info;
}

export type LoadCallback = (p: { status: 'init' | 'downloading' | 'ready' | 'error'; pct: number; detail?: string }) => void;

export async function loadOnDeviceKokoro(onProgress?: LoadCallback): Promise<any> {
  if (ttsPromise) return ttsPromise;
  ttsPromise = (async () => {
    // SharedArrayBuffer polyfill is in main.tsx (must run before any transformers/onnx import).

    // Configure transformers.js WASM backend before any kokoro-js import
    const trjs: any = await import('@huggingface/transformers');
    const trEnv = trjs.env;
    trEnv.backends.onnx.wasm.simd = false;
    trEnv.backends.onnx.wasm.numThreads = 1;

    // Transformers.js dtype map is frozen at module import time - we can't
    // extend it. To use HF variants it doesn't know about (model_q8f16.onnx
    // is the 86MB file the working Android/Expo Kokoro uses), we hook fetch
    // and rewrite the model filename at the network boundary.
    // Default target is q8f16; override via ?dtype= or localStorage.
    const extendedTargets: Record<string, string> = { q8f16: 'model_q8f16.onnx', uint8f16: 'model_uint8f16.onnx' };
    const dtypeWant = (new URLSearchParams(location.search).get('dtype') || localStorage.getItem('kokoroDtype') || 'q8f16').trim();
    const rewriteTo = extendedTargets[dtypeWant];
    try { (window as any).__doomLog?.('[kokoro] dtypeWant=' + dtypeWant + (rewriteTo ? ' via fetch-rewrite' : ' native')); } catch {}
    if (rewriteTo && !(window as any).__doomKokoroFetchHooked) {
      (window as any).__doomKokoroFetchHooked = true;
      const origFetch = window.fetch.bind(window);
      window.fetch = function (input: any, init?: any) {
        const url = typeof input === 'string' ? input : input?.url || String(input);
        if (/\/onnx\/model[^/]*\.onnx(\?.*)?$/i.test(url) && !url.includes(rewriteTo)) {
          const newUrl = url.replace(/\/onnx\/model[^/?]*\.onnx/, '/onnx/' + rewriteTo);
          try { (window as any).__doomLog?.('[kokoro] fetch rewrite -> ' + rewriteTo); } catch {}
          return origFetch(newUrl, init);
        }
        return origFetch(input, init);
      } as any;
    }

    const status = await detectOnDeviceCapability();
    const deviceOverride = (new URLSearchParams(location.search).get('device') || localStorage.getItem('kokoroDevice') || '').trim();
    const useWebGPU = deviceOverride === 'wasm' ? false : deviceOverride === 'webgpu' ? true : status === 'webgpu';
    const device = useWebGPU ? 'webgpu' : 'wasm';
    // iOS Safari memory + quality matrix (this HF repo):
    //   fp32 (326MB): correct but OOMs iOS Safari
    //   fp16 (163MB): dead-robot / OOM after cache accumulation
    //   q8=model_quantized (92MB): GIBBERISH - known bad quantization
    //   q4f16 (154MB): mixed int4+fp16, same size as fp16, different quant
    //   q8f16 (86MB): mixed q8+fp16 - the variant Android/Expo Kokoro uses
    // Default to q4f16 (smallest that preserves fp16 in critical layers).
    // Override: ?dtype=fp16|fp32|q8|q4|q4f16|q8f16|uint8|uint8f16
    const override = dtypeWant;
    const nativeAllowed = ['fp32', 'fp16', 'q8', 'q4', 'q4f16', 'uint8'];
    // For rewrite targets (q8f16, uint8f16) transformers.js still needs a
    // dtype it understands to produce a valid ORT session - pick the closest
    // native cousin; the rewritten fetch overrides the actual file it loads.
    const rewriteFallbackDtype: Record<string, string> = { q8f16: 'q8', uint8f16: 'uint8' };
    const dtype = (nativeAllowed.includes(override) ? override : (rewriteFallbackDtype[override] || 'q4f16')) as any;

    // Evict cached models from previous dtype tests so storage doesn't
    // accumulate (each test leaves ~100MB in Cache Storage and iOS Safari
    // starts OOMing after a couple of them). The file we want to keep is
    // either the rewrite target or the dtype-derived default filename.
    try {
      const keepFilename = rewriteTo
        || (dtype === 'fp32' ? 'model.onnx' : 'model_' + (dtype === 'q8' ? 'quantized' : dtype) + '.onnx');
      const cacheNames = await caches.keys();
      for (const name of cacheNames) {
        if (!/transformers|huggingface/i.test(name)) continue;
        const c = await caches.open(name);
        const reqs = await c.keys();
        for (const r of reqs) {
          if (/\/onnx\/model[^/]*\.onnx(\?.*)?$/i.test(r.url) && !r.url.includes(keepFilename)) {
            await c.delete(r);
          }
        }
      }
    } catch (e) { console.warn('[kokoro] cache prune failed', e); }
    onProgress?.({ status: 'init', pct: 2, detail: `Loading Kokoro (${device}, ${dtype})...` });
    const { KokoroTTS } = await import('kokoro-js');
    const tts = await KokoroTTS.from_pretrained('onnx-community/Kokoro-82M-v1.0-ONNX', {
      dtype: dtype as any,
      device: device as any,
      progress_callback: (p: any) => {
        if (p.status === 'progress' && p.total) {
          onProgress?.({ status: 'downloading', pct: Math.round((p.loaded / p.total) * 100), detail: p.file });
        } else if (p.status === 'done') {
          onProgress?.({ status: 'downloading', pct: 100, detail: p.file });
        }
      },
    });
    onProgress?.({ status: 'ready', pct: 100 });
    return tts;
  })().catch((e) => {
    ttsPromise = null;
    onProgress?.({ status: 'error', pct: 0, detail: String(e?.message || e) });
    throw e;
  });
  return ttsPromise;
}

const VOICE_MAP: Record<string, string> = {
  bella: 'af_bella', michael: 'am_michael', nicole: 'af_nicole',
  sarah: 'af_sarah', adam: 'am_adam', emma: 'bf_emma',
};

export type OnDeviceSpeakOpts = {
  voice: string;
  speed?: number;
  audioCtx: AudioContext;
  onLoad?: LoadCallback;
  onSentence?: (idx: number, total: number) => void;
  onEnd?: () => void;
  isActive: () => boolean; // returns false once caller session is stopped
};

function resampleLinear(pcm: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return pcm;
  const ratio = toRate / fromRate;
  const newLen = Math.floor(pcm.length * ratio);
  const out = new Float32Array(newLen);
  for (let i = 0; i < newLen; i++) {
    const srcIdx = i / ratio;
    const i0 = Math.floor(srcIdx);
    const i1 = Math.min(i0 + 1, pcm.length - 1);
    const frac = srcIdx - i0;
    out[i] = pcm[i0] * (1 - frac) + pcm[i1] * frac;
  }
  return out;
}

export async function speakOnDevice(sentences: string[], opts: OnDeviceSpeakOpts): Promise<AudioBufferSourceNode | null> {
  const dlog = (m: string) => { try { (window as any).__doomLog?.('[ondevice] ' + m); } catch {} };
  dlog('loading kokoro...');
  const tts = await loadOnDeviceKokoro(opts.onLoad);
  dlog('kokoro loaded, ctx sr=' + opts.audioCtx.sampleRate + ', generating ' + sentences.length + ' sentences');
  const ctx = opts.audioCtx;
  let currentSource: AudioBufferSourceNode | null = null;

  for (let i = 0; i < sentences.length; i++) {
    if (!opts.isActive()) { dlog('stopped at ' + i); return null; }
    opts.onSentence?.(i, sentences.length);
    let raw: any;
    try {
      const voiceId = VOICE_MAP[opts.voice] || (opts.voice.includes('_') ? opts.voice : 'af_bella');
      raw = await tts.generate(sentences[i], { voice: voiceId, speed: opts.speed ?? 1 });
      if (i === 0) dlog('first audio sr=' + raw?.sampling_rate + ' len=' + raw?.audio?.length + ' ctx=' + ctx.sampleRate);
    } catch (e: any) {
      dlog('generate[' + i + '] threw: ' + (e?.message || e));
      console.warn('[kokoro-ondevice] generate', e);
      continue;
    }
    if (!opts.isActive()) return null;
    const pcm: Float32Array = raw.audio;
    const rate: number = raw.sampling_rate;
    if (!pcm || !rate) continue;

    // iOS Safari WebAudio auto-resampler is unreliable for large ratios
    // (24k -> 48k doubles pitch / distorts). Resample ourselves to ctx rate.
    const targetRate = ctx.sampleRate;
    const resampled = rate !== targetRate ? resampleLinear(pcm, rate, targetRate) : pcm;
    if (i === 0 && rate !== targetRate) dlog('resampled ' + pcm.length + '->' + resampled.length);

    const buf = ctx.createBuffer(1, resampled.length, targetRate);
    buf.getChannelData(0).set(resampled);
    await new Promise<void>((resolve, reject) => {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      currentSource = src;
      src.onended = () => resolve();
      try { src.start(0); } catch (e) { reject(e); }
    });
  }
  if (opts.isActive()) opts.onEnd?.();
  return currentSource;
}
